import React, { useEffect, useState } from "react"
import { Label, Input } from "reactstrap"
import { useNavigate } from "react-router-dom"
import CurrencyInput from "react-currency-input-field"

import { useRedemptionData } from "../../../../Context/redemption"
import { usePurchaseData } from "../../../../Context/purchase"
import { RedeemCodes, AddToDonationCart, AccountTypes } from "../../../../Code/Data"
import { useAppData } from "../../../../Context/appData"

import { redeemCodeValidation, runOnceAfterwards, isTestRedeemCode } from "../../../../Code/Utilities"
import Modal from "../../../Shared/Modal"
import LoadingSpinner from "../../../Common/LoadingSpinner"
import { Button } from "../../../"
import AddToDonationCheckout from "../../../../Contents/ShoppingCart/AddToDonationCheckout"
import { srcPrepaidCard, srcBgOnboarding } from "../../../../images"
import styles from "./styles.module.scss"

function Step1({ onContinue, setSuccess, codeAsEntered: codeAsWasEntered, charityId, multi, rc, codes, setCodes, loggedInUser }) {
  const [errorMessage, setErrorMessage] = useState()
  const [codeAsEntered, setCodeAsEntered] = useState(codeAsWasEntered || "")
  const [code, setCode] = useState(codeAsWasEntered || "")
  const { fullCharityList } = useAppData()
  const { addToDonationData, setAddToDonationData } = usePurchaseData()
  const { amount } = addToDonationData
  const [codeValid, setCodeValid] = useState()
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [urlCharityId, setUrlCharityId] = useState(false)
  const [isModalOpen, setModalOpen] = useState(false)
  const [isActionModalOpen, setActionModalOpen] = useState(false)
  const [isTermsModalOpen, setTermsModalOpen] = useState(false)
  const [isMultiFieldDirty, setMultiFieldsDirty] = useState(!!codeAsWasEntered)
  const [directRedemption, setDirectRedemption] = useState(false)
  const [shoppingCart, setShoppingCart] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  let selectedCharity
  const {
    redemptionData, getRedeemCodeInfo, setRedemptionData, setRedeemCodeInfo, redeemCodeInfo, redirectOldCustomRedemptionPage
  } = useRedemptionData()
  const navigate = useNavigate()
  const normalizeCode = value => (value || "").toUpperCase().trim()
  const { BrandEcard, LogoURL, HideAmount } = redeemCodeInfo || {}
  const termsAndConditionsText = redeemCodeInfo?.DonorUserAccountType === AccountTypes.ThirdParty
    ? "CharityChoice is a fundraising project of Special Kids Fund (501c3, EIN 58-2550249), as such your confirmation and donation is 100% charitable. For gift cards purchased through third-party partners or redeeming points from rewards programs, when allocating funds to the chosen charities, we deduct a 10% fundraising fee, along with any applicable affiliate fees required by the third-party. Please consult your tax advisor regarding deductions and your specific tax situation."
    : "CharityChoice Gift Cards are recipient-designated charity gifts and a fundraising project of Special Kids Fund (a 501(c)(3) organization, EIN 58-2550249). Only the original gift recipient is entitled to designate the charity, which he or she may do at any time. Cards never lose value and do not expire. We always retain a reserve to honor unredeemed cards. Purchases of gift cards are charitable donations that are 100% tax-deductible at the time of purchase and, as such, are non-refundable. For cards purchased on our website, we deduct a 5% fundraising fee plus $0.50 per card (for cards over $2). Allocations are distributed quarterly in a single bulk check (usually within 4-6 weeks after the close of the quarter) for all designations made during that quarter."

  useEffect(() => {
    if (!multi) setErrorMessage(redeemCodeInfo?.ErrorMessage)
    if (charityId) {
      setUrlCharityId(charityId)
    }
  }, [])

  // Handle invalid format
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (errorMessage && !redeemCodeValidation(code)) {
        setCodeValid(false)
        if (isMultiFieldDirty) {
          setErrorMessage("The Redemption Code entered is not a valid CharityChoice Redemption Code.")
        }
      }
    }, 600)
    return () => clearTimeout(delayDebounceFn)
  }, [errorMessage])

  useEffect(() => {
    const doNow = async () => {
      const normalizedCode = normalizeCode(codeAsEntered)
      if (!normalizedCode) {
        setCodeValid(false)
        setErrorMessage("Please enter a Code.")
        return
      }
      let rci = redeemCodeInfo
      if (!rci || normalizeCode(rci?.RedeemCode) !== normalizedCode) {
        rci = await getRedeemCodeInfo(normalizedCode)
        // Stale-response guard: the context returns null when a newer
        // lookup superseded this one. Without this we throw
        // "Cannot read properties of null (reading 'Succeeded')".
        if (!rci) return
      }

      if (rci.HasCustomRedemptionPage) {
        redirectOldCustomRedemptionPage(rci);
      }
      if (charityId && !rci.CharityId) {
        rci.CharityId = charityId
        setUrlCharityId(charityId)
      }

      setCodeValid(rci.Succeeded && rci.IsValidCode !== false && !rci.HasRedeemed && !rci.ErrorMessage)

      if (rci.HasRedeemed) {
        setErrorMessage("The code has already been redeemed.")
      }
      else if (rci.ErrorMessage) {
        setErrorMessage(rci.ErrorMessage)
      }
      else if (rci.IsValidCode === false || !redeemCodeValidation(normalizedCode)) {
        setErrorMessage("The code is not a valid CharityChoice Redemption Code.")
      }
      else if (!rci.Succeeded) {
        setErrorMessage("Please check your code.")
      }
      else {
        setErrorMessage(null)
      }
    }

    doNow()
  }, [redeemCodeInfo, paymentSuccess])

  useEffect(() => {
    if (multi) {
      setCodes(rc?.split(","));
    }
    else {
      const normalizedCode = normalizeCode(rc)
      setCodeAsEntered(normalizedCode);
      setCode(normalizedCode)
    }
  }, [rc, multi])

  useEffect(() => {
    async function handleDirectRedemption() {
      if (directRedemption) {
        await RedeemDirectlyToCharity()
      }
    }

    handleDirectRedemption()
  }, [directRedemption])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (redeemCodeValidation(code) && codes.length > 0) {
        codes.push(code)
        setCode("")
      }
      if (!code) {
        setErrorMessage("Please enter a Code.")
      }
    }, 2000)
    return () => clearTimeout(timer)
  }, [code])

  const RedeemDirectlyToCharity = async () => {
    setDirectCharityId()
    const payload = {
      RedeemCode: redeemCodeInfo,
      Charities: selectedCharity ? [{
        CharityId: selectedCharity.CharityId,
        CharityName: selectedCharity.CharityName
      }] : [],
      ShareRedeemerInfo: redeemCodeInfo?.ShowDonor,
    }
    payload.Charities[0].Amount = +redeemCodeInfo.AmountToRedeem
    const { Succeeded, ErrorMessage } = await RedeemCodes(payload)
    if (selectedCharity) {
      setRedemptionData({
        ...redemptionData, Charities: [selectedCharity],
      })
    }
    if (Succeeded !== undefined && Succeeded) {
      setSuccess(true)
    }
    else {
      setErrorMessage(ErrorMessage)
    }
  }

  const handleChangeCode = async (newCode) => {
    const normalizedCode = normalizeCode(newCode)
    setCodeAsEntered(normalizedCode)

    if (!normalizedCode) {
      setCodeValid(false)
      setErrorMessage("Please enter a Code.")
      return
    }
    else if (!redeemCodeValidation(normalizedCode)) {
      setCodeValid(false)
      setErrorMessage(`${normalizedCode} is not a valid CharityChoice Redeem Code`)
      return
    }
    else {
      const rci = await getRedeemCodeInfo(normalizedCode)
      // Stale-response guard — same reason as the doNow useEffect above.
      if (!rci) return

      setCodeValid(rci.Succeeded && rci.IsValidCode !== false && !rci.HasRedeemed && !rci.ErrorMessage)

      if (rci.HasRedeemed) {
        setErrorMessage("The code has already been redeemed.")
      }
      else if (rci.ErrorMessage) {
        setErrorMessage(rci.ErrorMessage)
      }
      else if (rci.IsValidCode === false) {
        setErrorMessage("The code is not a valid CharityChoice Redemption Code.")
      }
      else if (!rci.Succeeded) {
        setErrorMessage("Please check your code.")
      }
      else {
        setErrorMessage(null)

        if (rci.HasCustomRedemptionPage) {
          redirectOldCustomRedemptionPage(rci);
        }
        if (rci.HasCharityId) {
          setDirectRedemption(true)
        }
        if (rci.UserRedemptionPageInfo) {
          navigate(`/Redeem/Custom/${rci.UserRedemptionPageInfo.UserId}/?RC=${encodeURIComponent(normalizedCode)}`)
        }
      }
    }
  }

  const handleChangeAmount = event => {
    if (event === undefined) {
      event = 0
    }
    setAddToDonationData({
      ...addToDonationData, amount: event,
    })
  }
  const setDirectCharityId = () => {
    if (!redeemCodeInfo?.CharityId && (charityId || urlCharityId)) {
      const rci = redeemCodeInfo
      if (rci && !rci.CharityId) {
        rci.CharityId = parseInt(charityId || urlCharityId)
      }
      setRedeemCodeInfo({ ...rci })
    }
    if (!fullCharityList || fullCharityList.length === 0) {
      return
    }
    selectedCharity = fullCharityList.find(({ CharityId }) => parseInt(CharityId) === parseInt(redeemCodeInfo.CharityId))
  }
  const handleAddCode = async () => {
    const result = await getRedeemCodeInfo(code, loggedInUser.UserId)
    if (redeemCodeValidation(code) && result.Succeeded) {
      codes.push(code)
      const nonEmptyCodes = codes.filter((value) => value.trim() !== "")

      setCodes([...nonEmptyCodes])
      setCode("")
    }
    else {
      setErrorMessage(!code
        ? "Please enter a Code."
        : `${code} is not a valid CharityChoice Redeem Code`)
    }
  }
  const handleChangeMultiCode = index => e => {
    if (!isMultiFieldDirty) setMultiFieldsDirty(true)
    const inputValue = e.target.value.toUpperCase()
    const updatedCodes = codes.filter(value => value !== "")
    updatedCodes[index] = inputValue
    updatedCodes.filter(code => code !== "")
    setCodes(...[updatedCodes])
    setCode(null)
    if (index === codes.length) {
      clearInputById(`${index}`)
    }
    isValid()
  }
  const isValid = () => {
    if (multi) {
      const uniqueValues = new Set(codes)
      let valid = agreeTerms && codes.length > 1 && codes.length === uniqueValues.size
      let errorMessage
      for (let i = 0; i < codes.length; i++) {
        if (!redeemCodeValidation(codes[i])) {
          valid = false
          errorMessage = `${codes[i]} is not a valid CharityChoice Redeem Code`
          break
        }
      }
      if (!valid) {
        if (errorMessage === undefined && isMultiFieldDirty) {
          errorMessage = (codes.length < 1
            ? "This feature is for redemption of multiple codes only."
            : (codes.length !== uniqueValues.size
              ? "Unable to process redeem a code more than once"
              : "Please agree to our terms and conditions"))
        }

      }
      return valid
    }
    else {
      return agreeTerms && codeValid
    }
  }
  const handleChangeEmail = e => {
    const updatedShareInfo = redemptionData.ShareInfo
      ? { ...redemptionData.ShareInfo, email: e.target.value }
      : { email: e.target.value }
    setRedemptionData({
      ...redemptionData,
      email: e.target.value,
      ShareInfo: updatedShareInfo
    })
  }
  const handleContinue = async () => {
    if (amount > 0 && !paymentSuccess) {
      if (!isActionModalOpen) {
        setActionModalOpen(true)
        return
      }
      else {
        setActionModalOpen(false)
        setAddToDonationData({
          ...addToDonationData, amount: 0,
        })
      }
    }
    setRedemptionData({
      ...redemptionData, code: multi ? codes.join(",") : code,
    })
    if (redeemCodeInfo?.HasCharityId || urlCharityId || charityId) {
      setDirectCharityId()
      const payload = {
        RedeemCode: redeemCodeInfo.RedeemCode,
        Charities: selectedCharity ? [{
          CharityId: selectedCharity.CharityId,
          CharityName: selectedCharity.CharityName
        }] : [],
        ShareRedeemerInfo: redeemCodeInfo?.ShowDonor,
      }
      if (payload.Charities.length > 0) {
        payload.Charities[0].Amount = +redeemCodeInfo.AmountToRedeem
        const { Succeeded, ErrorMessage } = await RedeemCodes(payload)
        if (Succeeded) {
          setRedemptionData({
            ...redemptionData,
            Charities: payload.Charities
          })
          setSuccess(true)
        }
        else {
          setErrorMessage(ErrorMessage);
        }
      }
      else {
        setErrorMessage("Your redemption could not be completed. Please contact us if this issue recurrs")
      }
      return
    }
    onContinue()
  }
  const clearInputById = (id) => {
    const inputElement = document.getElementById(id)

    if (inputElement) {
      inputElement.value = ""
    }
  }
  const handlePaymentSuccess = () => {
    setPaymentSuccess(true)
  }
  const onPay = async () => {
    const result = await AddToDonationCart(amount)
    if (result?.Succeeded) {
      // The cart can contain prior add-to-donation items (Items[0] in the
      // response is often a leftover from earlier). The just-added item is
      // always at the end of the list — pick that one.
      const items = result.ShoppingCart?.Items || []
      setShoppingCart(items[items.length - 1] || null)
      setModalOpen(true)
    }
    setActionModalOpen(false)
  }
  const renderRedeemInput = () => {
    const logoImage = ((BrandEcard && LogoURL) ? <div className={styles.title}>
      <img className={styles.imgLogo} src={LogoURL} alt="Corporate Logo" />
    </div> : null)

    // Coerce AmountToRedeem to a real number. The backend sometimes returns a
    // pre-formatted string like "$xx" (e.g. for test codes), which would
    // produce "You have $$xx to redeem!" if we interpolated it directly.
    const amountToRedeemNum = parseFloat(String(redeemCodeInfo?.AmountToRedeem ?? "").replace(/[^0-9.]/g, ""))
    const hasValidAmount = Number.isFinite(amountToRedeemNum) && amountToRedeemNum > 0
    const totalToRedeem = paymentSuccess
      ? amountToRedeemNum + (parseFloat(amount) || 0)
      : amountToRedeemNum
    const title = hasValidAmount && HideAmount === false && !!codeValid
      ? `You have $${totalToRedeem.toFixed(2).replace(/\.00$/, "")} to redeem!`
      : "Let's redeem your Charity Gift!"
    return (<>
      {logoImage}
      {paymentSuccess &&
        <span className={styles.caption}>You have added ${amount || redeemCodeInfo?.AddedToDonationAmount} to this donation.</span>}
      <div className={styles.title}>{title}</div>
      <img
        className={styles.imgCard}
        src={redeemCodeInfo?.ImageUrl || srcPrepaidCard}
        alt=""
      />
      <div className={styles.redemptionDetail}>
        <div className={styles.inputWrapper}>
          <input
            className={styles.inputCode}
            name="code"
            maxLength={15}
            value={code || ""}
            onChange={e => setCode(e.target.value.toUpperCase().trim())}
            onKeyUp={e => {
              const val = e.target.value.toUpperCase().trim();
              runOnceAfterwards(() => handleChangeCode(val), 3000)
            }}
            onBlur={e => {
              const val = e.target.value.toUpperCase().trim();
              if (val && val !== codeAsEntered?.toUpperCase().trim()) {
                handleChangeCode(val);
              }
            }}
            placeholder="Redeem Code"
          />
          <div className={styles.linkChangeCode} onClick={() => handleChangeCode(code)}>{!!code ? "Change Code" : "Enter Code"}</div>
        </div>
        <div className={styles.inputWrapper}>
          <input
            className={styles.inputEmail}
            style={{ fontSize: redemptionData?.email ? "130%" : "32px" }}
            name="email"
            value={redemptionData?.email || ""}
            placeholder="Email Address"
            onChange={handleChangeEmail}
          />
          <div className={styles.lblEmail}>
            Optional: To receive a confirmation
          </div>
        </div>
      </div>
    </>)
  }

  const renderMultiRedeemInput = () => {
    return (
      <>
        <div className={styles.title}>Enter Your Redemption Codes</div>
        {codes.filter(code => code !== "")
          .map((code, index) => (
            <div key={index} className={styles.multiInputWrapper}>
              <input
                className={styles.multiInputCode}
                name={`code-${index}`}
                placeholder="Input Code"
                value={code}
                onChange={handleChangeMultiCode(index)}
              />
            </div>
          ))}
        <div className={styles.multiInputWrapper}>
          <input
            className={styles.multiInputCode}
            name="code"
            placeholder="Input Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
          <i className="fa fa-plus-circle" onClick={handleAddCode} />

        </div>
      </>
    )
  }
  return (
    <div className={styles.container}>
      <Modal
        isOpen={isActionModalOpen}
        onClose={() => setActionModalOpen(false)}
        contentLabel="Action dialog"
      >
        <p className="fs-4">Did you change your mind about adding ${amount} to this donation?</p>
        <button className={styles.yesBtn} onClick={() => handleContinue()}>Yes, Continue</button>
        <button className={styles.noBtn} onClick={() => onPay()}>No, Add ${amount} Now</button>
      </Modal>

      <Modal
        isOpen={isModalOpen}
        onClose={() => { setModalOpen(false); setShoppingCart(null) }}
        contentLabel="Add to Donation Checkout"
      >
        <AddToDonationCheckout item={shoppingCart} amount={amount} onPaymentSuccess={handlePaymentSuccess}
          code={code} />
      </Modal>
      <Modal
        isOpen={isTermsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        contentLabel="Terms and Conditions"
      >
        <h3 className="mb-3">Terms and Conditions</h3>
        <p>{termsAndConditionsText}</p>
      </Modal>
      <div className={styles.content} style={{ "--bg-onboarding": `url(${srcBgOnboarding})` }}>
        {multi ? renderMultiRedeemInput() : renderRedeemInput()}
        {(!codeValid)
          ? (!!errorMessage
            ? <div className={styles.errorMessage}>{errorMessage}</div>
            : <LoadingSpinner />)
          : <>
            <Label check className={styles.termsWrapper}>
              <Input
                className={styles.termsRadio}
                type="radio"
                checked={agreeTerms === true}
                onChange={() => setAgreeTerms(!agreeTerms)}
              />{" "}
              <span className={styles.lblTerms}>
                I affirm that I am the gift recipient or that I received this code
                in a rewards program by me redeeming my points. View our &nbsp;
                <button
                  type="button"
                  className={styles.termsLinkButton}
                  onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    setTermsModalOpen(true)
                  }}>
                  Terms and Conditions
                </button>.
              </span>
            </Label>
            <Button
              className={`${styles.btnContinue} m-3`}
              disabled={!isValid()}
              onClick={handleContinue}>
              Continue
            </Button>
            {codeValid && !multi && redeemCodeInfo?.HasCharityId !== true && !paymentSuccess &&
              <>
                {amount > 0 && isTestRedeemCode(codeAsWasEntered) &&
                  <div className={styles.addToDonationTestMsg}>To add, please use a non-test redemption code</div>
                }
                <div className={styles.divAddToDonation}>
                  <div>
                    To add to this donation, enter an amount
                  </div>
                  <div
                    title={"When you designate your charity gift, you may add your own funds to increase the benefit for your favorite charities. The total sum of your charity gift and whatever you have added, will be divided equally amongst the chosen charities. You will receive an emailed tax receipt for the additional funds."}>
                    <CurrencyInput className={styles.amountInput} onValueChange={handleChangeAmount}
                      allowNegativeValue={false} prefix={"$"} placeholder="$0"
                      value={amount ? amount.toString() : ""} />
                  </div>
                  {amount > 0 && !isTestRedeemCode(codeAsWasEntered) &&
                    <div className={styles.addToDonationBtn} onClick={() => onPay()}> Add ${amount} Now </div>
                  }
                </div>
              </>
            }

          </>
        }
      </div>
    </div >)
}

export default Step1

